import { useState, Fragment, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation, Trans } from "react-i18next";
import { Dialog, Transition } from "@headlessui/react";
import { useAuthStore } from "../../../stores/authStore";
import { SocialLoginButtons } from "./SocialLoginButtons";
import { SocialLoginStatus, SocialStatus } from "../../../components/common/SocialLoginStatus";
import { Logo } from "../../../components/common/Logo";
import { motion, AnimatePresence } from "framer-motion";

interface RegisterModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSwitchToLogin?: () => void;
    prefillEmail?: string;
}

export const RegisterModal = ({ isOpen, onClose, onSwitchToLogin, prefillEmail }: RegisterModalProps) => {
    const { t } = useTranslation("common");
    const [searchParams, setSearchParams] = useSearchParams();
    const [view, setView] = useState<'options' | 'email'>(prefillEmail ? 'email' : 'options');

    useEffect(() => {
        if (isOpen && prefillEmail) {
            setView('email');
        }
    }, [isOpen, prefillEmail]);

    // Schema without name
    const registerSchema = z
        .object({
            email: z.string().email(t("errors.invalidEmail") as string),
            password: z.string().min(8, t("errors.passwordTooShort") as string),
            confirmPassword: z.string().min(1, t("errors.confirmPassword") as string),
        })
        .refine((data) => data.password === data.confirmPassword, {
            message: t("errors.passwordsDontMatch") as string,
            path: ["confirmPassword"],
        });

    type RegisterFormData = z.infer<typeof registerSchema>;

    const navigate = useNavigate();
    const {
        register: registerUser,
        isLoading,
        error,
        clearError,
    } = useAuthStore();

    const {
        register,
        handleSubmit,
        formState: { errors },
        clearErrors,
        reset
    } = useForm<RegisterFormData>({
        resolver: zodResolver(registerSchema),
        defaultValues: {
            email: prefillEmail || "",
        },
        mode: "onBlur",
        reValidateMode: "onBlur"
    });

    useEffect(() => {
        if (!isOpen) {
            // Immediately clear errors to prevent them showing during transition
            clearErrors();
            clearError();

            // Delay resetting view slightly to avoid flicker during closing transition
            const timer = setTimeout(() => {
                setView(prefillEmail ? 'email' : 'options');
                reset();
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [isOpen, prefillEmail, reset, clearError, clearErrors]);

    const handleBack = () => {
        reset();
        clearError();
        clearErrors();
        setView('options');
    };

    const [socialStatus, setSocialStatus] = useState<SocialStatus | null>(null);
    const [socialProvider, setSocialProvider] = useState<string>('');

    const handleSocialLogin = async (provider: string, token: string) => {
        try {
            setSocialProvider(provider);
            setSocialStatus('processing');
            clearError();
            await useAuthStore.getState().socialLogin(provider, token);

            setSocialStatus('success');

            setTimeout(() => {
                const { user } = useAuthStore.getState();
                const skipVerification = window.__DASHBAL_CONFIG__?.skipEmailVerification;
                const redirectPath = (user && !user.emailVerified && !skipVerification)
                    ? "/check-email"
                    : "/boards";

                // The route change unmounts the modal — do not call onClose().
                // onClose() = LandingPage's setSearchParams({}), whose internal
                // navigate("?") uses LandingPage's captured pathname ("/") and
                // would navigate back to "/", undoing this redirect.
                navigate(redirectPath, { replace: true });

                setTimeout(() => setSocialStatus(null), 500);
            }, 1200);
        } catch (err: any) {
            setSocialStatus('error');
            setTimeout(() => {
                setSocialStatus(null);
            }, 3000);
            // Error handled in store
        }
    };

    const onSubmit = async (data: RegisterFormData) => {
        try {
            clearError();

            // We pass name as empty string or let backend generate it.
            const splitName = data.email.split('@')[0];

            await registerUser({
                email: data.email,
                password: data.password,
                name: splitName // Backend overrides this anyway
            });

            const { user } = useAuthStore.getState();
            const skipVerification = window.__DASHBAL_CONFIG__?.skipEmailVerification;
            const redirectPath = (user && !user.emailVerified && !skipVerification)
                ? "/check-email"
                : "/boards";

            // The route change unmounts the modal — do not call onClose().
            // onClose() = LandingPage's setSearchParams({}), whose internal
            // navigate("?") uses LandingPage's captured pathname ("/") and
            // would navigate back to "/", undoing this redirect.
            navigate(redirectPath, { replace: true });
        } catch (err) {
            // Error is already set in the store
        }
    };


    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-[60]" onClose={() => {
                clearErrors();
                onClose();
            }}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 modal-backdrop" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="relative bg-white dark:bg-neutral-900 rounded-xl shadow-2xl max-w-md w-full transform transition-all overflow-hidden">
                                <AnimatePresence>
                                    {socialStatus && (
                                        <SocialLoginStatus 
                                            status={socialStatus} 
                                            provider={socialProvider}
                                            errorMessage={error ? t(error) : undefined} 
                                        />
                                    )}
                                </AnimatePresence>
                                <div className="px-6 pb-6 pt-10">
                                    <div className="flex justify-center mb-8 h-8">
                                        <h2 className="relative text-2xl font-semibold text-text-primary dark:text-neutral-100">
                                            <div className="absolute right-full mr-3 flex items-center gap-2 whitespace-nowrap h-full">
                                                <Logo className="w-7 h-7" />
                                                <span className="w-4 h-4 text-neutral-300 dark:text-neutral-600" style={{maskImage: `url(https://cdn.dashbal.com/assets/icon_89a1da25.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_89a1da25.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block'}} role='img' />
                                            </div>
                                            {t("auth.signUp")}
                                        </h2>
                                    </div>

                                    {/* View 1: Select Method */}
                                    {view === 'options' ? (
                                        <div className="space-y-4">
                                            <SocialLoginButtons onSocialLogin={handleSocialLogin} isLoading={isLoading} />

                                            <button
                                                type="button"
                                                onClick={() => setView('email')}
                                                disabled={isLoading}
                                                className="relative flex items-center justify-center w-full px-4 py-3 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-800 hover:bg-neutral-50 dark:hover:bg-neutral-750 transition-colors disabled:opacity-50 disabled:cursor-not-allowed group"
                                            >
                                                <div className="absolute left-4 text-neutral-500 dark:text-neutral-400">
                                                    <span className="w-5 h-5" style={{maskImage: `url(https://cdn.dashbal.com/assets/icon_17b85f1b.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_17b85f1b.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block'}} role='img' />
                                                </div>
                                                <span className="text-sm font-medium text-neutral-700 dark:text-neutral-200">
                                                    {t("auth.continueWithEmail", "Continue with Email")}
                                                </span>
                                            </button>
                                        </div>
                                    ) : (
                                        /* View 2: Email Form (All in one) */
                                        <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
                                            <div className="flex items-center mb-4">
                                                <button
                                                    type="button"
                                                    onClick={handleBack}
                                                    className="text-sm text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200 flex items-center absolute left-6 top-10"
                                                >
                                                    <span className="w-5 h-5" style={{maskImage: `url(https://cdn.dashbal.com/assets/icon_8a57af8b.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_8a57af8b.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block'}} role='img' />
                                                </button>
                                            </div>

                                            <AnimatePresence>
                                                {error && (
                                                    <motion.div
                                                        initial={{ opacity: 0, y: -8 }}
                                                        animate={{ 
                                                            opacity: 1, 
                                                            y: 0,
                                                            x: [0, -3, 3, -3, 3, 0]
                                                        }}
                                                        exit={{ opacity: 0, scale: 0.95 }}
                                                        transition={{ duration: 0.2 }}
                                                        className="mb-6 p-4 rounded-xl bg-red-50 dark:bg-red-500/10 border border-red-100 dark:border-red-500/20"
                                                    >
                                                        <div className="flex items-start gap-3">
                                                            <span className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" style={{maskImage: `url(https://cdn.dashbal.com/assets/icon_3c00880f.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_3c00880f.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block'}} role='img' />
                                                            <div className="flex-1">
                                                                <p className="text-sm font-medium text-red-700 dark:text-red-400 leading-snug">
                                                                    {t(error)}
                                                                </p>
                                                                {error === 'userErrors.auth.emailAlreadyExists' && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={(e) => {
                                                                            e.preventDefault();
                                                                            clearError();
                                                                            if (onSwitchToLogin) {
                                                                                onSwitchToLogin();
                                                                            } else {
                                                                                setSearchParams({ ...Object.fromEntries(searchParams), modal: "login" });
                                                                            }
                                                                        }}
                                                                        className="mt-2 block text-xs font-bold text-red-800 dark:text-red-300 hover:underline underline-offset-2"
                                                                    >
                                                                        {t("userErrors.auth.signInInstead")}
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </motion.div>
                                                )}
                                            </AnimatePresence>

                                            <div>
                                                <label htmlFor="email" className="block text-sm font-medium text-text-primary dark:text-neutral-200">
                                                    {t("auth.email")}
                                                </label>
                                                <div className="mt-1">
                                                    <input
                                                        {...register("email", { 
                                                            onChange: () => {
                                                                clearError();
                                                                clearErrors("email");
                                                            } 
                                                        })}
                                                        id="register-email"
                                                        name="email"
                                                        type="email"
                                                        autoComplete="email"
                                                        className={`input ${(errors.email || error === 'userErrors.auth.emailAlreadyExists') ? "input-error" : ""}`}
                                                        placeholder={t("auth.emailPlaceholder") as string}
                                                        autoFocus={!prefillEmail}
                                                    />
                                                    {errors.email && (
                                                        <p className="mt-1 text-sm text-red-600 dark:text-red-400 font-medium">{errors.email.message}</p>
                                                    )}
                                                </div>
                                            </div>

                                            <div>
                                                <label htmlFor="password" className="block text-sm font-medium text-text-primary dark:text-neutral-200">
                                                    {t("auth.password")}
                                                </label>
                                                <div className="mt-1">
                                                    <input
                                                        {...register("password", { 
                                                            onChange: () => {
                                                                clearError();
                                                                clearErrors("password");
                                                            } 
                                                        })}
                                                        id="register-password"
                                                        name="password"
                                                        type="password"
                                                        autoComplete="new-password"
                                                        className={`input ${errors.password ? "input-error" : ""}`}
                                                        placeholder={t("auth.passwordPlaceholder") as string}
                                                        autoFocus={!!prefillEmail}
                                                    />
                                                    {errors.password && (
                                                        <p className="mt-1 text-sm text-red-600 dark:text-red-400 font-medium">{errors.password.message}</p>
                                                    )}
                                                </div>
                                            </div>

                                            <div>
                                                <label htmlFor="confirmPassword" className="block text-sm font-medium text-text-primary dark:text-neutral-200">
                                                    {t("auth.confirmPassword")}
                                                </label>
                                                <div className="mt-1">
                                                    <input
                                                        {...register("confirmPassword", { 
                                                            onChange: () => {
                                                                clearError();
                                                                clearErrors("confirmPassword");
                                                            } 
                                                        })}
                                                        id="register-confirm-password"
                                                        name="confirmPassword"
                                                        type="password"
                                                        autoComplete="new-password"
                                                        className={`input ${errors.confirmPassword ? "input-error" : ""}`}
                                                        placeholder={t("auth.passwordPlaceholder") as string}
                                                    />
                                                    {errors.confirmPassword && (
                                                        <p className="mt-1 text-sm text-red-600 dark:text-red-400 font-medium">{errors.confirmPassword.message}</p>
                                                    )}
                                                </div>
                                            </div>

                                            <button
                                                type="submit"
                                                disabled={isLoading}
                                                className="btn-primary w-full py-3"
                                            >
                                                {isLoading ? (
                                                    <div className="flex items-center justify-center">
                                                        <div className="spinner mr-2" />
                                                        {t("actions.loading")}
                                                    </div>
                                                ) : (
                                                    t("auth.signUp")
                                                )}
                                            </button>

                                            <div className="text-center px-4">
                                                <p className="text-[11px] leading-relaxed text-neutral-500 dark:text-neutral-400">
                                                    <Trans
                                                        i18nKey="auth.registrationAgreement"
                                                        ns="common"
                                                        components={[
                                                            <Link key="tos" to="/terms" className="text-accent-primary hover:underline" />,
                                                            <Link key="privacy" to="/privacy" className="text-accent-primary hover:underline" />
                                                        ]}
                                                    />
                                                </p>
                                            </div>
                                        </form>
                                    )}

                                    <div className="text-center mt-6 pt-4 border-t border-neutral-100 dark:border-neutral-800">
                                        <span className="text-sm text-neutral-600 dark:text-neutral-400">
                                            {t("auth.hasAccount")}{" "}
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    if (onSwitchToLogin) {
                                                        onSwitchToLogin();
                                                    } else {
                                                        setSearchParams({ ...Object.fromEntries(searchParams), modal: "login" });
                                                    }
                                                }}
                                                className="font-medium text-accent-primary hover:text-accent-primary/80"
                                            >
                                                {t("auth.signIn")}
                                            </button>
                                        </span>
                                    </div>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};
